// stores/localStorageCache.js - Pinia store for localStorage caching and billing buffer management
// Ensures singleton state management across component lifecycles

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useApiClient } from '@/services/apiClient'

export const useLocalStorageCacheStore = defineStore('localStorageCache', () => {
  const { baseFetch } = useApiClient()
  
  // localStorage configuration constants
  const LOCAL_STORAGE_SIZE_LIMIT = 5 * 1024 * 1024 // 5MB
  // The cache's ONE bound: a write that would take the origin's localStorage
  // past this evicts the oldest entries FIRST, so ordinary use never writes
  // until the browser refuses. The headroom under the limit covers what the
  // bound cannot see or free: another tab's writes, and a browser whose quota
  // is counted tighter than ours. Characters, like every size in this store.
  const LOCAL_STORAGE_WRITE_BUDGET = LOCAL_STORAGE_SIZE_LIMIT * 0.8
  const CACHE_BASE_PREFIX = 'tower_hunt_cache' // Base prefix for all cache entries
  const CACHE_TTL_MS = 10 * 60 * 1000 // 10 minutes default TTL
  
  // Cache namespaces for different data types
  const CACHE_NAMESPACES = {
    FILTERED_DATA: 'filtered_data',    // filteredData.js — table_page + stats query results
    VISUALIZATION: 'visualization',    // propertyCollectionVisualization.js — stable SVG geometry
    LAYER_STYLING: 'layer_styling',    // The Skyline's layer tier: each layer's served values and statements, keyed by layer
    SIZE_DATA: 'size_data',            // no writer — FilterUnlockPanel clears legacy entries only
    MAP_DATA: 'map_data',              // mapDataService.js, fogOfWarService — map entities
    USER_DATA: 'user_data',            // workspaceLayout.js — user preferences (unchanged)
    TIDE_CHART: 'tide_chart',          // tideChartVisualization.js — tide chart data
    GLANCE_SUMMARY: 'glance_summary',  // useGlanceSummary.js — map entity/cluster glance tooltip payloads (short TTL, per-band)
    RECORD_CONTEXT: 'record_context',  // useEventContext.js — expanded event card context slices (short TTL, per-band)
    MEDIA_MANIFEST: 'media_manifest',  // useMediaManifest.js — host-scoped gallery manifests (short TTL, per-band; manifest JSON only, image bytes cache via immutable URLs)
    SPACE_CANVAS_FA_DETAIL: 'space_canvas_fa_detail',          // useFaDetailData.js — SpaceCanvas floor-area detail / cells
    SPACE_CANVAS_LCL_AGGREGATE: 'space_canvas_lcl_aggregate',  // useLclAggregateData.js — SpaceCanvas LCL aggregates
    TASK_DATA: 'task_data',            // tasks.js — task lists, tasks, view preferences
    MESSAGES: 'messages',              // weMessages.js — discussion message windows per topic
    TRACK_RECORD: 'track_record'       // Track Record shelves + card pages (expertise-progression D10; per-band)
  }

  // PAYLOAD SCHEMA VERSION, per namespace. A namespace whose cached payload SHAPE
  // changes on the wire (a key retired, an array folded into another) names a
  // version here, and `generateCacheKey` folds it into EVERY key in that
  // namespace — so a browser still holding the old shape MISSES on the new key
  // instead of hydrating a payload the reader no longer understands, and does so
  // at once rather than when the entry's TTL runs out. Bump the number when the
  // shape changes again; a namespace absent from this table emits no version
  // segment and its keys are byte-identical to what they were.
  //
  // ONE mechanism in the ONE key builder, rather than a version constant per
  // composable: the two Space Canvas namespaces are its first members because
  // fadvo-void-fold retired the `fadvos: […]` array from the LCL-aggregate and
  // FA-detail payloads (every opening is a `Void` row now), and a cached
  // pre-fold envelope would have rendered those openings as ABSENT for up to an
  // hour. Reads, writes and targeted clears all go through the same builder, so
  // no consumer knows the version exists.
  //
  // TRACK_RECORD is at 3: its card payload is assembled across several
  // expertise-progression beads, and each reshape bumps this number rather than
  // waiting out a TTL. 3 because a shelf's figures carry no building-grain whole
  // and carry the better-of composite and amenities by kind through the public
  // face, so no browser serves an earlier shape for the hour the namespace holds one.
  //
  // LAYER_STYLING is at 3: the layer tier carries each layer's codes and statements,
  // keyed by layer ({ page, buildings, plates }), so no browser keeps a code painted
  // from a whole figure, or a blob with no statements, for the rest of its hour.
  //
  // Served-absence-statements moves TIDE_CHART to 2, VISUALIZATION to 6,
  // SPACE_CANVAS_FA_DETAIL to 2, SPACE_CANVAS_LCL_AGGREGATE to 4, TRACK_RECORD to 4 and
  // GLANCE_SUMMARY to 5: each payload states its absences (`statements`) beside its
  // values, so a payload cached without them is unreachable under the new key.
  //
  // MESSAGES is versioned because the WIA card reshape (topicId / topicType,
  // messageReference, forwardedMessage) lands across several beads, and each
  // reshape bumps this number rather than waiting out the TTL.
  //
  // TIDE_CHART and VISUALIZATION are versioned because their payloads carry the
  // detail-cap disclosure (`detail_cap`, `detail_cap_truncated_categories`) only
  // when a listing was cut, so ABSENT KEYS MEAN NOT TRUNCATED: a payload cached
  // before the server sent the arm would read as an uncut chart or Skyline for
  // the rest of its hour. VISUALIZATION is at schema 8: the Skyline payload carries
  // structural-hop-banding's shape (rows of unknown links dropped, known-and-unread
  // rows stamped `parent_link_read: false`), the palette's served colours
  // (app-design-standard D13) and the measured partition widths, and holds no span
  // map because the request's frame divides a shared plate, and each level carries the
  // `extent_unknown` stamp (a level whose width nobody can read draws as a ghost), so no
  // browser keeps drawing an earlier shape, colour, whole figure or unstamped level for
  // the cache's hour. SPACE_CANVAS_LCL_AGGREGATE is at 3: 2 for the same palette reason, 3 because
  // a plate keyed to a right off the level's roster now carries its key, marked
  // `controlling_right_listed: false` (th-4c6vth), so no cached Space Canvas paints a
  // colour its legend no longer shows or greys a plate Focus says is controlled.
  //
  // FILTERED_DATA and GLANCE_SUMMARY are versioned because the asking_rent table
  // cell and glance entry are a QUOTE LIST ([{basis, low, high, source}]) under
  // pro-forma-lease-economics, which a Float-shaped envelope never meets.
  //
  // FILTERED_DATA is at 4: every table figure cell carries its server identity,
  // its subject triple and its focal `calculation` descriptor (calculated-value-
  // figures D2, D13), a shared plate's cell holds the figure the viewer's frame
  // divided, and the cashflow series carries its composition, statement records
  // and focal_host. A page cached without them, or with a whole figure, is
  // unreachable under the `schema:4` key.
  //
  // GLANCE_SUMMARY is at 4 for the same reason: every glance figure carries its
  // server identity (its subject triple and its focal descriptor, cvf E6a), the card
  // hands those figures its own request as their host (cvf E6), the figure is the
  // frame's divided one, and it states actual lines only and divides by
  // Cashflow::HostSize. A card cached without them would open its figures as bare
  // elements, or show a whole figure, so it is unreachable under the `schema:4` key.
  //
  // RECORD_CONTEXT is versioned from 1 for the same reason: every figure on an expanded
  // event card (its composed lines and its cash-flow figures) carries its server
  // identity (cvf E4, E4c, E6a), and the slice hands those figures its own request as
  // their host and its own rung as their one Unlock (cvf E8, founder ruling 14). A slice
  // cached without them, under a key with no schema component, would open its figures as
  // bare elements, so it is unreachable under the `schema:1` key.
  //
  // capital-constrained-space moves TIDE_CHART to 4, VISUALIZATION to 9,
  // SPACE_CANVAS_FA_DETAIL to 3, SPACE_CANVAS_LCL_AGGREGATE to 5, FILTERED_DATA to 5,
  // GLANCE_SUMMARY to 7 and LAYER_STYLING to 5, each one past main's: a listing's
  // capital-distress reading rides those payloads (the `constrained` listing state and
  // its `locked` / `unfunded` availability on the Skyline, the Tide Chart and both
  // Space Canvas views; the capital-distress figure and chips on table rows and glance
  // cards), so no browser draws a zombie listing `live` for the hour a namespace holds one.
  //
  // filter-locked-record-identity moves TIDE_CHART to 5, VISUALIZATION to 10 and
  // LAYER_STYLING to 6, each one past main's: an encumbered record reaches those payloads
  // with a sealed handle in place of its id, and a filter-locked Skyline card is its
  // placeholder (one statement, no right, building or plate beneath it), so no browser
  // keys, draws or links a filter-locked record by an id it cached for the hour.
  // FILTERED_DATA and TRACK_RECORD have no writer of that shape, so they move nothing.
  //
  // reference-datasets moves GLANCE_SUMMARY to 8, one past main's: a Property or building
  // card gains its Location section (the walkability figure), so no browser serves a card
  // cached without it for the minute a namespace holds one.
  const CACHE_NAMESPACE_SCHEMA_VERSIONS = Object.freeze({
    [CACHE_NAMESPACES.TIDE_CHART]: 5,
    [CACHE_NAMESPACES.VISUALIZATION]: 10,
    [CACHE_NAMESPACES.SPACE_CANVAS_FA_DETAIL]: 3,
    [CACHE_NAMESPACES.SPACE_CANVAS_LCL_AGGREGATE]: 5,
    [CACHE_NAMESPACES.TRACK_RECORD]: 5,
    [CACHE_NAMESPACES.MESSAGES]: 1,
    [CACHE_NAMESPACES.FILTERED_DATA]: 5,
    [CACHE_NAMESPACES.GLANCE_SUMMARY]: 8,
    [CACHE_NAMESPACES.LAYER_STYLING]: 6,
    [CACHE_NAMESPACES.RECORD_CONTEXT]: 1
  })

  // The "core data-band" namespaces cleared by every COARSE invalidation — a
  // wipe that has no element-level key list to target and so drops the whole
  // banded surface: an auth transition (guest→user / user-switch), an unlock
  // whose backend named no specific namespaces, the cross-actor share-grant
  // open-tab re-band (share-grants FE cable→cache-bust bridge), and a funding
  // rise no open tab heard (actorAvailableBalancesChannel, sas D-12). ONE list, all
  // readers — the th-xfbaf TIDE_CHART leak was a drift between two
  // hand-maintained copies of exactly this set; keeping a single source makes
  // that class of drift impossible. USER_DATA is deliberately EXCLUDED (user
  // preferences are not banded and must survive these wipes).
  const coreDataNamespaces = [
    CACHE_NAMESPACES.FILTERED_DATA,
    CACHE_NAMESPACES.VISUALIZATION,
    CACHE_NAMESPACES.TIDE_CHART,
    CACHE_NAMESPACES.GLANCE_SUMMARY,
    CACHE_NAMESPACES.RECORD_CONTEXT,
    CACHE_NAMESPACES.MEDIA_MANIFEST,
    CACHE_NAMESPACES.SPACE_CANVAS_FA_DETAIL,
    CACHE_NAMESPACES.SPACE_CANVAS_LCL_AGGREGATE,
    CACHE_NAMESPACES.LAYER_STYLING,
    CACHE_NAMESPACES.SIZE_DATA,
    CACHE_NAMESPACES.MAP_DATA,
    CACHE_NAMESPACES.TRACK_RECORD,
    CACHE_NAMESPACES.MESSAGES
  ]

  // ================================
  // STATE - Persistent across app lifecycle
  // ================================
  
  // Core cache state
  // Coerce to a real boolean. The previous expression `typeof Storage !==
  // 'undefined' && localStorage` short-circuited to the localStorage object
  // (truthy but not boolean), which broke external consumers that read
  // `cacheStore.available` (Pinia auto-unwraps refs in setup stores) and
  // expected a boolean — e.g. fogOfWarService's persistUgiCache /
  // loadUgiCache guards silently early-returned, so the UGI cache stayed
  // unpersisted across reloads even though the in-memory mirror was fine.
  const available = ref(typeof Storage !== 'undefined' && !!localStorage)
  const enablePersistence = ref(true)
  
  // NEW: In-memory LRU cache for ultra-fast repeated access
  const recentAccessCache = ref(new Map())
  const RECENT_CACHE_MAX_SIZE = 100 // Keep only most recent items in memory
  
  // NEW: Request deduplication to prevent simultaneous identical requests
  const activeRequests = ref(new Map())
  
  // Storage metrics
  const storageMetrics = ref({
    totalSize: 0,
    lastEviction: null,
    operations: 0,
    cacheHits: 0,
    cacheMisses: 0,
    successfulOperations: 0,
    memoryHits: 0,  // NEW: Track in-memory cache hits
    dedupedRequests: 0  // NEW: Track deduplicated requests
  })
  
  // CRITICAL: Tracking buffer state - prevents double-billing
  const trackingBuffer = ref(new Map()) // Key: "record_type:record_id:element_name:element_type", Value: expiration timestamp
  const bufferedUntil = ref(null) // Global buffer expiration for this user session
  
  // ================================
  // COMPUTED PROPERTIES
  // ================================
  
  const cacheHitRate = computed(() => {
    const totalOperations = storageMetrics.value.operations || 1
    const cacheHits = storageMetrics.value.cacheHits || 0
    return Math.round((cacheHits / totalOperations) * 100)
  })
  
  const storageUtilization = computed(() => {
    const utilization = (storageMetrics.value.totalSize / LOCAL_STORAGE_SIZE_LIMIT) * 100
    return Math.min(utilization, 100).toFixed(1)
  })
  
  // ================================
  // TRACKING BUFFER ACTIONS
  // ================================
  
  /**
   * Generate tracking buffer key for an element combination
   * @param {Object} combination - Element combination object
   * @returns {string} Buffer key
   */
  const generateTrackingBufferKey = (combination) => {
    return `${combination.record_type}:${combination.record_id}:${combination.element_name}:${combination.element_type}`
  }

  /**
   * Check if an element is currently in the tracking buffer
   * @param {Object} combination - Element combination object
   * @returns {boolean} True if element is already being tracked
   */
  const isElementInBuffer = (combination) => {
    const bufferKey = generateTrackingBufferKey(combination)
    const expirationTime = trackingBuffer.value.get(bufferKey)
    
    if (!expirationTime) {
      return false
    }
    
    // Check if buffer entry has expired
    if (Date.now() > expirationTime) {
      // Clean up expired entry
      trackingBuffer.value.delete(bufferKey)
      return false
    }
    
    return true
  }

  /**
   * Add elements to tracking buffer with expiration time
   * @param {Array} elementCombinations - Element combinations to buffer
   * @param {string} bufferedUntilTimestamp - Expiration timestamp from server
   */
  const addElementsToTrackingBuffer = (elementCombinations, bufferedUntilTimestamp) => {
    const expirationTime = new Date(bufferedUntilTimestamp).getTime()
    
    elementCombinations.forEach(combination => {
      const bufferKey = generateTrackingBufferKey(combination)
      trackingBuffer.value.set(bufferKey, expirationTime)
    })
    
    // Update global buffer expiration
    bufferedUntil.value = bufferedUntilTimestamp
    
    console.log(`[LocalStorageStore] Added ${elementCombinations.length} elements to tracking buffer, expires: ${bufferedUntilTimestamp}`)
  }

  /**
   * Filter out elements that are already in the tracking buffer
   * @param {Array} elementCombinations - All element combinations
   * @returns {Array} Only combinations not currently in buffer
   */
  const filterUnbufferedElements = (elementCombinations) => {
    const unbuffered = elementCombinations.filter(combination => {
      return !isElementInBuffer(combination)
    })
    
    return unbuffered
  }

  /**
   * Clear tracking buffer (for auth transitions, etc.)
   */
  const clearTrackingBuffer = () => {
    trackingBuffer.value.clear()
    bufferedUntil.value = null
    console.log(`[LocalStorageStore] Cleared tracking buffer`)
  }
  
  // ================================
  // IN-MEMORY LRU CACHE ACTIONS
  // ================================

  // The memory tier is ONE LRU (`recentAccessCache`) addressed two ways:
  // - the ENTITY pair keys an entry `<entityType>_<entityId>`, outside any
  //   namespace;
  // - the KEYED trio keys an entry exactly as `storeData(namespace, { key })`
  //   keys the same value in localStorage — `generateCacheKey(namespace, { key })`,
  //   i.e. `tower_hunt_cache:<namespace>:key:<key>` (plus `:schema:<n>` in a
  //   versioned namespace). So one logical key names a value on both tiers,
  //   and `clearCacheWhere` / `clearCacheByNamespace` reach both through the
  //   one logical-key reader (`logicalCacheKey`). An entity key never starts
  //   with the cache prefix, so a namespace clear never touches one.

  /**
   * Read an entry from the memory LRU, marking it most recently used
   * @param {string} cacheKey - Internal memory key
   * @returns {any|null} Cached value or null
   */
  const readMemoryEntry = (cacheKey) => {
    if (recentAccessCache.value.has(cacheKey)) {
      storageMetrics.value.memoryHits++
      // Move to end (most recently used)
      const entry = recentAccessCache.value.get(cacheKey)
      recentAccessCache.value.delete(cacheKey)
      recentAccessCache.value.set(cacheKey, entry)
      return entry
    }

    return null
  }

  /**
   * Write an entry to the memory LRU, evicting the least recently used when full
   * @param {string} cacheKey - Internal memory key
   * @param {any} data - Value to cache
   */
  const writeMemoryEntry = (cacheKey, data) => {
    // Remove if already exists (to update position)
    if (recentAccessCache.value.has(cacheKey)) {
      recentAccessCache.value.delete(cacheKey)
    }

    // Implement LRU eviction if cache is full
    if (recentAccessCache.value.size >= RECENT_CACHE_MAX_SIZE) {
      const firstKey = recentAccessCache.value.keys().next().value
      recentAccessCache.value.delete(firstKey)
    }

    recentAccessCache.value.set(cacheKey, data)
  }

  /**
   * Get entity from in-memory cache
   * @param {string} entityType - Type of entity (e.g., 'property', 'investment')
   * @param {string|number} entityId - Entity ID
   * @returns {Object|null} Cached entity or null
   */
  const getMemoryCachedEntity = (entityType, entityId) => {
    return readMemoryEntry(`${entityType}_${entityId}`)
  }

  /**
   * Set entity in in-memory cache with LRU eviction
   * @param {string} entityType - Type of entity
   * @param {string|number} entityId - Entity ID
   * @param {Object} data - Entity data to cache
   */
  const setMemoryCachedEntity = (entityType, entityId, data) => {
    writeMemoryEntry(`${entityType}_${entityId}`, data)
  }

  /**
   * Clear entity from in-memory cache
   * @param {string} entityType - Type of entity
   * @param {string|number} entityId - Entity ID
   */
  const clearMemoryCachedEntity = (entityType, entityId) => {
    const cacheKey = `${entityType}_${entityId}`
    recentAccessCache.value.delete(cacheKey)
  }

  /**
   * Internal memory key of a keyed entry — the localStorage key of the same
   * (namespace, key), so both tiers share one address (see above)
   * @param {string} namespace - Cache namespace
   * @param {string} key - Logical key
   * @returns {string} Internal memory key
   */
  const keyedMemoryKey = (namespace, key) => generateCacheKey(namespace, { key })

  /**
   * Get a value from the in-memory cache by namespace and logical key — the
   * memory-tier peer of `fetchData(namespace, { key })`
   * @param {string} namespace - Cache namespace
   * @param {string} key - Logical key
   * @returns {any|null} Cached value or null
   */
  const getMemoryCachedKey = (namespace, key) => {
    return readMemoryEntry(keyedMemoryKey(namespace, key))
  }

  /**
   * Set a value in the in-memory cache by namespace and logical key, with LRU
   * eviction — the memory-tier peer of `storeData(namespace, { key }, value)`
   * @param {string} namespace - Cache namespace
   * @param {string} key - Logical key
   * @param {any} value - Value to cache
   */
  const setMemoryCachedKey = (namespace, key, value) => {
    writeMemoryEntry(keyedMemoryKey(namespace, key), value)
  }

  /**
   * Clear one keyed value from the in-memory cache
   * @param {string} namespace - Cache namespace
   * @param {string} key - Logical key
   */
  const clearMemoryCachedKey = (namespace, key) => {
    recentAccessCache.value.delete(keyedMemoryKey(namespace, key))
  }

  /**
   * Clear all in-memory cache
   */
  const clearMemoryCache = () => {
    recentAccessCache.value.clear()
  }
  
  // ================================
  // REQUEST DEDUPLICATION ACTIONS
  // ================================
  
  /**
   * Check if a request is already in flight
   * @param {string} requestKey - Unique key for the request
   * @returns {Promise|null} The existing promise if request is in flight
   */
  const getActiveRequest = (requestKey) => {
    return activeRequests.value.get(requestKey) || null
  }
  
  /**
   * Register an active request to prevent duplicates
   * @param {string} requestKey - Unique key for the request
   * @param {Promise} promise - The request promise
   */
  const setActiveRequest = (requestKey, promise) => {
    activeRequests.value.set(requestKey, promise)
    
    // Clean up after promise resolves or rejects
    promise.finally(() => {
      activeRequests.value.delete(requestKey)
    }).catch(() => {
      // Silently catch to prevent unhandled rejection
    })
  }
  
  /**
   * Generate a request key for deduplication
   * @param {string} type - Request type (e.g., 'entity', 'batch')
   * @param {Object} params - Request parameters
   * @returns {string} Unique request key
   */
  const generateRequestKey = (type, params) => {
    const sortedParams = Object.entries(params)
      .filter(([, value]) => value !== null && value !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => {
        if (typeof value === 'object') {
          return `${key}:${JSON.stringify(value)}`
        }
        return `${key}:${value}`
      })
      .join('|')
    
    return `request:${type}:${sortedParams}`
  }
  
  /**
   * Wrap a request with deduplication
   * @param {string} requestKey - Unique key for the request
   * @param {Function} requestFn - Function that returns a promise
   * @returns {Promise} The deduplicated request promise
   */
  const deduplicatedRequest = async (requestKey, requestFn) => {
    // Check if request is already in flight
    const existingRequest = getActiveRequest(requestKey)
    if (existingRequest) {
      storageMetrics.value.dedupedRequests++
      console.log(`[LocalStorageStore] Deduplicating request: ${requestKey}`)
      return existingRequest
    }
    
    // Create new request and register it
    const requestPromise = requestFn()
    setActiveRequest(requestKey, requestPromise)
    
    return requestPromise
  }
  
  // ================================
  // CORE LOCALSTORAGE ACTIONS
  // ================================
  
  /**
   * Generate a generic cache key with namespace support
   * @param {string} namespace - The cache namespace (e.g., 'market', 'size_data')
   * @param {Object} keyComponents - Object with key-value pairs for cache key generation
   * @returns {string} The cache key
   */
  const generateCacheKey = (namespace, keyComponents) => {
    // The namespace's payload schema version rides the key as one more
    // component (`schema:<n>`), so a shape change misses every old entry.
    const schemaVersion = CACHE_NAMESPACE_SCHEMA_VERSIONS[namespace]
    const components = schemaVersion === undefined
      ? keyComponents
      : { ...keyComponents, schema: schemaVersion }
    const sortedComponents = Object.entries(components)
      .filter(([, value]) => value !== null && value !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}:${value}`)
      .join(':')
    
    return `${CACHE_BASE_PREFIX}:${namespace}:${sortedComponents}`
  }
  
  /**
   * Check if an error is a QuotaExceededError
   * @param {Error} error - The error to check
   * @returns {boolean} True if quota exceeded
   */
  const isQuotaExceededError = (error) => {
    return (
      error.name === 'QuotaExceededError' ||
      error.code === 22 ||        // Chrome/Safari
      error.code === 1014 ||      // Firefox
      error.name === 'NS_ERROR_DOM_QUOTA_REACHED'  // Firefox older
    )
  }

  /**
   * Store generic data in localStorage with namespace support
   * Automatically handles QuotaExceededError by evicting old entries and retrying
   * @param {string} namespace - The cache namespace
   * @param {Object} keyComponents - Components for cache key generation
   * @param {any} data - The data to cache
   * @param {Object} options - Cache options (ttl, userId, etc.)
   */
  const storeData = (namespace, keyComponents, data, options = {}) => {
    if (!available.value || !data) {
      return null
    }

    const cacheKey = generateCacheKey(namespace, keyComponents)
    const { ttl = CACHE_TTL_MS, userId = null } = options

    const cacheData = {
      data,
      metadata: {
        namespace,
        keyComponents,
        userId,
        cached_at: Date.now(),
        ttl
      }
    }

    const jsonString = JSON.stringify(cacheData)

    // Bound the store BELOW the quota before writing (th-ocl0u4); the quota
    // catch below stays as the backstop for what the bound cannot see.
    if (!reserveStorageRoom(cacheKey, jsonString)) {
      return null
    }

    // Attempt storage with retry after eviction
    const attemptStore = (retryCount = 0) => {
      try {
        localStorage.setItem(cacheKey, jsonString)

        // Track in metrics
        storageMetrics.value.operations++
        storageMetrics.value.successfulOperations++

        return cacheKey

      } catch (error) {
        // Handle QuotaExceededError specifically
        if (isQuotaExceededError(error)) {
          if (retryCount < 2) {
            console.warn(`[LocalStorageStore] Quota exceeded, evicting old entries (attempt ${retryCount + 1})`)

            // Evict at least 20% of limit or double the size of new data
            const targetBytes = Math.max(
              LOCAL_STORAGE_SIZE_LIMIT * 0.2,
              jsonString.length * 2
            )
            evictCacheEntries(targetBytes)

            return attemptStore(retryCount + 1)
          }

          console.error(`[LocalStorageStore] Quota exceeded after ${retryCount} eviction attempts, giving up`)
        } else {
          console.error(`[LocalStorageStore] Failed to store data:`, error)
        }

        return null
      }
    }

    return attemptStore()
  }

  /**
   * Fetch generic data from localStorage with namespace support
   * @param {string} namespace - The cache namespace
   * @param {Object} keyComponents - Components for cache key generation
   * @param {Object} options - Fetch options (userId validation, etc.)
   */
  const fetchData = (namespace, keyComponents, options = {}) => {
    if (!available.value) {
      return null
    }
    
    const cacheKey = generateCacheKey(namespace, keyComponents)
    const { userId = null } = options
    
    try {
      const cachedData = localStorage.getItem(cacheKey)
      if (!cachedData) {
        storageMetrics.value.cacheMisses++
        return null
      }
      
      const parsed = JSON.parse(cachedData)
      
      // Check TTL
      const cacheAge = Date.now() - (parsed.metadata?.cached_at || 0)
      if (cacheAge > (parsed.metadata?.ttl || CACHE_TTL_MS)) {
        // Cache expired
        localStorage.removeItem(cacheKey)
        console.log(`[LocalStorageStore] Cache expired for key: ${cacheKey}`)
        return null
      }
      
      // Validate user if specified
      if (userId && parsed.metadata?.userId && parsed.metadata.userId !== userId) {
        console.warn(`[LocalStorageStore] User mismatch for key: ${cacheKey}`)
        return null
      }
      
      storageMetrics.value.cacheHits++
      return parsed.data
      
    } catch (error) {
      console.error(`[LocalStorageStore] Failed to fetch data:`, error)
      return null
    }
  }

  // ================================
  // CACHE ACCESS TRACKING ACTIONS
  // ================================
  
  /**
   * Build element combinations for cache access tracking
   * Converts monetizable elements metadata to BufferedBillingService format
   * @param {Array} entities - Cached entities
   * @param {Array} monetizableElements - Monetizable elements metadata
   * @returns {Array} Element combinations for tracking
   */
  const buildElementCombinationsForCache = (entities, monetizableElements) => {
    const combinations = []
    
    entities.forEach((entity) => {
      // Find matching monetizable elements for this entity
      const entityMetadata = monetizableElements.find(m => {
        return m.record_type === (entity.type || entity._entityType) && 
               m.record_id === entity.id
      })
      
      if (entityMetadata && entityMetadata.elements) {
        entityMetadata.elements.forEach(element => {
          const combination = {
            record_type: entityMetadata.record_type,
            record_id: entityMetadata.record_id,
            element_name: element.name,
            element_type: element.type
          }
          combinations.push(combination)
        })
      }
    })
    
    return combinations
  }

  /**
   * Track cache access asynchronously (deferred)
   * Default behavior to maintain cache performance
   * @param {Array} elementCombinations - Elements to track
   */
  const trackCacheAccessDeferred = async (elementCombinations) => {
    try {
      // Validate input
      if (!elementCombinations || elementCombinations.length === 0) {
        return
      }
      
      // Use baseFetch from apiClient for consistency with V2 API
      const response = await baseFetch('/cache_access/track', {
        method: 'POST',
        body: JSON.stringify({
          element_combinations: elementCombinations
        })
      })
      
      if (response.success) {
        // Cache access tracking successful
        
        // Update tracking buffer with accurate server response
        if (response.buffered_until && response.tracked_elements > 0) {
          // Update buffer expiration from server
          addElementsToTrackingBuffer(elementCombinations, response.buffered_until)
        }
      } else {
        console.warn('[LocalStorageStore] Cache access tracking failed:', response.status, response.statusText)
      }
    } catch (error) {
      console.warn('[LocalStorageStore] Deferred cache access tracking error:', error)
    }
  }

  /**
   * Track cache access immediately (synchronous)
   * Use sparingly for critical cases as it can block cache performance
   * @param {Array} elementCombinations - Elements to track
   */
  const trackCacheAccessImmediate = async (elementCombinations) => {
    try {
      // Use baseFetch from apiClient for consistency with V2 API
      const response = await baseFetch('/cache_access/track', {
        method: 'POST',
        body: JSON.stringify({
          element_combinations: elementCombinations
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      const result = await response.json()
      console.log(`[LocalStorageStore] Immediate cache access tracking successful: ${result.tracked_elements} elements tracked`)
      
      // Update tracking buffer with accurate server response
      if (result.buffered_until && result.tracked_elements > 0) {
        console.log(`[LocalStorageStore] Updating buffer expiration from server: ${result.buffered_until}`)
        addElementsToTrackingBuffer(elementCombinations, result.buffered_until)
      }
      
      return result
    } catch (error) {
      console.error('[LocalStorageStore] Immediate cache access tracking failed:', error)
      throw error
    }
  }

  /**
   * Track cache access for monetizable data elements
   * Integrates with BufferedBillingService for proper creator compensation
   * Prevents duplicate tracking of elements already in the 5-minute buffer
   * @param {Array} entities - Cached entities that were accessed
   * @param {Array} monetizableElements - Monetizable elements metadata from SizeCalculationService
   * @param {Object} options - Tracking options (deferred, apiResponseData, etc.)
   */
  const trackCacheAccess = async (entities, monetizableElements, options = {}) => {
    if (!entities || !monetizableElements) {
      console.warn('[LocalStorageStore] trackCacheAccess: Missing required parameters')
      return
    }
    
    try {
      // CRITICAL: Check if API response includes buffered_until and populate frontend buffer
      // This prevents unnecessary cache_access/track calls for elements that are already buffered on the backend
      const apiResponseData = options.apiResponseData
      const bufferedUntil = apiResponseData?.buffered_until || apiResponseData?.billing_summary?.buffered_until
      if (bufferedUntil) {
        const allElementCombinations = buildElementCombinationsForCache(entities, monetizableElements)
        
        // IMPORTANT: Only add elements to buffer that aren't already buffered
        // This prevents perpetual buffer renewal which would break monetization
        const unbufferedElements = filterUnbufferedElements(allElementCombinations)
        if (unbufferedElements.length > 0) {
          addElementsToTrackingBuffer(unbufferedElements, bufferedUntil)
          console.log(`[LocalStorageStore] Added ${unbufferedElements.length} new elements to tracking buffer from API response, buffered until: ${bufferedUntil}`)
        } else {
          console.log(`[LocalStorageStore] All ${allElementCombinations.length} elements from API response already buffered, not updating expiration`)
        }
      }
      
      // Build element combinations for BufferedBillingService
      const allElementCombinations = buildElementCombinationsForCache(entities, monetizableElements)
      
      // Filter out elements already in the tracking buffer (including those just added above)
      const unbufferedElementCombinations = filterUnbufferedElements(allElementCombinations)
      
      // Debug info for tracking behavior
      const bufferedCount = allElementCombinations.length - unbufferedElementCombinations.length
      
      if (unbufferedElementCombinations.length > 0) {
        console.log(`[LocalStorageStore] Tracking ${unbufferedElementCombinations.length} new cache access elements (${bufferedCount} already buffered)`)
        
        // Immediately add elements to buffer to prevent race conditions
        // Use 5-minute default buffer duration (will be updated with accurate server response)
        const defaultBufferedUntil = new Date(Date.now() + 5 * 60 * 1000).toISOString()
        addElementsToTrackingBuffer(unbufferedElementCombinations, defaultBufferedUntil)
        
        // Call backend to track cache access (this will update buffer with accurate server timing)
        if (options.deferred !== false) {
          // Async tracking to not block cache performance (default behavior)
          setTimeout(() => trackCacheAccessDeferred(unbufferedElementCombinations), 0)
        } else {
          // Immediate tracking for critical cases
          await trackCacheAccessImmediate(unbufferedElementCombinations)
        }
      } else {
        console.log(`[LocalStorageStore] All ${allElementCombinations.length} elements already buffered - skipping cache access tracking`)
      }
    } catch (error) {
      console.warn('[LocalStorageStore] Cache access tracking failed:', error)
      // Don't block cache retrieval on tracking errors
    }
  }

  // ================================
  // LOCALSTORAGE EVICTION ACTIONS
  // ================================

  /**
   * Get all cache entries with metadata for eviction decisions
   * @returns {Array} Array of cache entry metadata objects
   */
  const getAllCacheEntries = () => {
    const entries = []
    const prefix = CACHE_BASE_PREFIX

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(prefix)) {
        try {
          const rawData = localStorage.getItem(key)
          const data = JSON.parse(rawData)
          entries.push({
            key,
            size: rawData.length,
            cachedAt: data.metadata?.cached_at || 0,
            ttl: data.metadata?.ttl || CACHE_TTL_MS,
            namespace: data.metadata?.namespace
          })
        } catch {
          // Corrupted entry, mark for cleanup
          entries.push({ key, size: localStorage.getItem(key)?.length || 0, cachedAt: 0, corrupted: true })
        }
      }
    }
    return entries
  }

  /**
   * Evict oldest/expired entries to free up space
   * Priority: 1) corrupted entries, 2) expired entries, 3) oldest entries (LRU)
   * @param {number} targetBytesToFree - Minimum bytes to free
   * @returns {Object} { evictedCount, freedBytes }
   */
  const evictCacheEntries = (targetBytesToFree) => {
    const entries = getAllCacheEntries()
    let freedBytes = 0
    let evictedCount = 0

    const now = Date.now()

    // Sort by eviction priority: corrupted first, then expired, then oldest
    const sorted = entries.sort((a, b) => {
      // Corrupted entries first
      if (a.corrupted && !b.corrupted) return -1
      if (!a.corrupted && b.corrupted) return 1

      // Expired entries next
      const aExpired = now - a.cachedAt > a.ttl
      const bExpired = now - b.cachedAt > b.ttl
      if (aExpired && !bExpired) return -1
      if (!aExpired && bExpired) return 1

      // Then by age (oldest first - LRU)
      return a.cachedAt - b.cachedAt
    })

    for (const entry of sorted) {
      if (freedBytes >= targetBytesToFree) break

      localStorage.removeItem(entry.key)
      freedBytes += entry.size
      evictedCount++
    }

    storageMetrics.value.lastEviction = Date.now()
    console.log(`[LocalStorageStore] Evicted ${evictedCount} entries, freed ${freedBytes} bytes`)

    return { evictedCount, freedBytes }
  }

  /**
   * Characters the origin's localStorage holds, keys included — every key, not
   * only the cache's, because the browser's quota counts them all
   * @returns {number} Characters in use
   */
  const localStorageUsage = () => {
    let used = 0
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key === null) continue
      used += key.length + (localStorage.getItem(key)?.length || 0)
    }
    return used
  }

  /**
   * Make room for one write under LOCAL_STORAGE_WRITE_BUDGET, evicting the
   * oldest cache entries before the write rather than after the browser
   * refuses it. One bound for every namespace: a family of large entries (a
   * Skyline page is ~400K characters) is held to it like any other.
   * @param {string} cacheKey - Key about to be written
   * @param {string} jsonString - Serialized value about to be written
   * @returns {boolean} False when the entry alone exceeds the budget (not cacheable)
   */
  const reserveStorageRoom = (cacheKey, jsonString) => {
    const incoming = cacheKey.length + jsonString.length
    if (incoming > LOCAL_STORAGE_WRITE_BUDGET) {
      return false
    }

    try {
      // An overwrite replaces its own previous value, which is not new use.
      const replaced = localStorage.getItem(cacheKey)
      const replacedSize = replaced === null ? 0 : cacheKey.length + replaced.length
      const overBudget = localStorageUsage() - replacedSize + incoming - LOCAL_STORAGE_WRITE_BUDGET
      if (overBudget > 0) {
        evictCacheEntries(overBudget)
      }
    } catch (error) {
      // Measuring must never cost the write; the quota catch still guards it.
      console.warn('[LocalStorageStore] Could not measure storage before a write:', error)
    }

    return true
  }

  /**
   * Clean up expired entries proactively
   * @returns {number} Number of entries cleaned up
   */
  const cleanupExpiredEntries = () => {
    const entries = getAllCacheEntries()
    const now = Date.now()
    let cleanedCount = 0

    for (const entry of entries) {
      if (entry.corrupted || now - entry.cachedAt > entry.ttl) {
        localStorage.removeItem(entry.key)
        cleanedCount++
      }
    }

    if (cleanedCount > 0) {
      console.log(`[LocalStorageStore] Cleaned up ${cleanedCount} expired/corrupted entries`)
    }

    return cleanedCount
  }

  /**
   * Get current localStorage usage statistics
   * @returns {Object} { totalSize, entryCount, utilizationPercent }
   */
  const getStorageStats = () => {
    const entries = getAllCacheEntries()
    const totalSize = entries.reduce((sum, entry) => sum + entry.size, 0)

    return {
      totalSize,
      entryCount: entries.length,
      utilizationPercent: Math.min((totalSize / LOCAL_STORAGE_SIZE_LIMIT) * 100, 100).toFixed(1)
    }
  }

  // ================================
  // INITIALIZATION AND CLEANUP
  // ================================
  
  /**
   * Initialize the store
   */
  const initialize = () => {
    // Clean up expired entries on startup to prevent quota issues
    const cleanedCount = cleanupExpiredEntries()
    if (cleanedCount > 0) {
      console.log(`[LocalStorageStore] Startup cleanup: removed ${cleanedCount} expired entries`)
    }

    // Listen for auth transitions to clear ALL user-scoped caches
    // This prevents guest cache from persisting after login and handles user switches
    window.addEventListener('authTransition', (event) => {
      const { type, previousUserId, currentUserId } = event.detail
      console.log(`[LocalStorageStore] Auth transition detected: ${type}`, { previousUserId, currentUserId })

      // Clear tracking buffer (existing behavior)
      clearTrackingBuffer()

      // Clear the core data-band namespaces + in-memory mirror to prevent stale
      // user data (handles every store that doesn't include user context in its
      // cache keys). Routed through the single coarse-invalidation routine so
      // the auth-transition and unlock paths clear an identical surface and can
      // never drift — the th-xfbaf TIDE_CHART leak was exactly a drift between
      // two hand-maintained copies of this list (see `coreDataNamespaces`).
      const { invalidated } = invalidateCoreDataCaches()
      console.log(`[LocalStorageStore] Cleared ${invalidated} cache entries across core data namespaces + in-memory cache due to auth transition`)
    })
  }
  
  // Initialize when store is created
  initialize()
  
  // ================================
  // CROSS-SERVICE CACHE COORDINATION FUNCTIONS
  // ================================
  
  // The `key:` marker `generateCacheKey` writes ahead of a `{ key }` component.
  const LOGICAL_KEY_MARKER = 'key:'

  /**
   * The LOGICAL key of a cache entry: its raw key (localStorage or keyed
   * memory — both come from `generateCacheKey`) with the namespace prefix and
   * a leading `key:` marker stripped. An entry stored under
   * `{ key: 'property_visualization:v2:ids:1,2:page:1:detail_level:full:per_page:50:layer:-:order:properties:sort:-:asof:-:scale:fit' }`
   * (a Skyline ids-lane page, keyed by `utils/skylineRequestShape.js`) has
   * exactly that logical key.
   *
   * In a versioned namespace the builder appends `:schema:<n>` after a `{ key }`
   * component (`key` sorts ahead of `schema`), and that segment is stripped here
   * too: the version is the builder's, never part of the key a consumer wrote,
   * so a predicate that PARSES its logical key (the Skyline's
   * `parseVisualizationKey`, an exact inverse of its own builder) reads the
   * same string in a versioned namespace as in an unversioned one.
   * @param {string} namespace - Cache namespace the raw key belongs to
   * @param {string} namespacePrefix - `tower_hunt_cache:<namespace>:`
   * @param {string} rawKey - Raw key that starts with the namespace prefix
   * @returns {string} Logical key
   */
  const logicalCacheKey = (namespace, namespacePrefix, rawKey) => {
    const rest = rawKey.slice(namespacePrefix.length)
    if (!rest.startsWith(LOGICAL_KEY_MARKER)) return rest

    const logicalKey = rest.slice(LOGICAL_KEY_MARKER.length)
    const schemaVersion = CACHE_NAMESPACE_SCHEMA_VERSIONS[namespace]
    const schemaSuffix = `:schema:${schemaVersion}`
    return schemaVersion !== undefined && logicalKey.endsWith(schemaSuffix)
      ? logicalKey.slice(0, -schemaSuffix.length)
      : logicalKey
  }

  /**
   * Whether a logical key matches a `clearCacheByNamespace` pattern
   * @param {string} logicalKey - Logical key
   * @param {string|null} keyPattern - No pattern or `'*'` alone matches every
   *   key; a trailing `*` is a PREFIX match; anything else is a substring match
   * @returns {boolean} True when the key matches
   */
  const matchesKeyPattern = (logicalKey, keyPattern) => {
    if (!keyPattern || keyPattern === '*') return true
    if (keyPattern.endsWith('*')) return logicalKey.startsWith(keyPattern.slice(0, -1))
    return logicalKey.includes(keyPattern)
  }

  /**
   * Clear every entry of a namespace whose LOGICAL key the predicate accepts,
   * on BOTH tiers: localStorage and the keyed memory tier. Entity-keyed memory
   * entries sit outside every namespace and are never touched.
   * @param {string} namespace - Cache namespace to clear
   * @param {(logicalKey: string) => boolean} predicate - Called with each logical key
   * @returns {number} Number of entries cleared across both tiers
   */
  const clearCacheWhere = (namespace, predicate) => {
    const namespacePrefix = `${CACHE_BASE_PREFIX}:${namespace}:`
    const matches = (rawKey) =>
      rawKey.startsWith(namespacePrefix) && predicate(logicalCacheKey(namespace, namespacePrefix, rawKey))

    let clearedCount = 0

    // Memory first, so a localStorage failure below never leaves it stale
    for (const key of Array.from(recentAccessCache.value.keys())) {
      if (matches(key)) {
        recentAccessCache.value.delete(key)
        clearedCount++
      }
    }

    if (!available.value) return clearedCount

    try {
      // Collect first: removing while indexing localStorage shifts the indices
      const keysToRemove = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && matches(key)) {
          keysToRemove.push(key)
        }
      }

      for (const key of keysToRemove) {
        localStorage.removeItem(key)
        clearedCount++
      }
    } catch (error) {
      console.error(`[LocalStorageCache] Failed to clear namespace '${namespace}':`, error)
    }

    return clearedCount
  }

  /**
   * Clear the entries of a namespace whose LOGICAL key matches a pattern, on
   * both tiers (see `clearCacheWhere`)
   * @param {string} namespace - Cache namespace to clear
   * @param {string} keyPattern - Optional pattern: none or `'*'` alone clears
   *   every entry, a trailing `*` is a prefix match (`'property_visualization:*'`),
   *   anything else is a substring match
   * @returns {number} Number of entries cleared across both tiers
   */
  const clearCacheByNamespace = (namespace, keyPattern = null) => {
    const clearedCount = clearCacheWhere(namespace, (logicalKey) => matchesKeyPattern(logicalKey, keyPattern))

    if (clearedCount > 0) {
      console.log(`[LocalStorageCache] Cleared ${clearedCount} entries from namespace '${namespace}' with pattern '${keyPattern || '*'}'`)
    }

    return clearedCount
  }
  
  /**
   * Coarse-clear the full core data-band surface: every `coreDataNamespaces`
   * entry plus the in-memory mirror. This is the single routine behind every
   * element-key-less invalidation — the auth-transition wipe, the unlock
   * fallback (backend named no targeted namespaces), and the cross-actor
   * share-grant open-tab re-band. Safe as a coarse wipe because clearing is
   * cheap (localStorage.removeItem) and caches repopulate on next access via
   * TTL-based fetching.
   *
   * @returns {{ invalidated: number, services: Array, errors: Array }}
   */
  const invalidateCoreDataCaches = () => {
    const results = { invalidated: 0, services: [], errors: [] }

    for (const ns of coreDataNamespaces) {
      try {
        const cleared = clearCacheByNamespace(ns, '*')
        if (cleared > 0) {
          results.invalidated += cleared
          results.services.push({ namespace: ns, cleared })
        }
      } catch (error) {
        console.error(`[LocalStorageCache] Failed to clear namespace '${ns}':`, error)
        results.errors.push({ namespace: ns, error: error.message })
      }
    }

    // Always clear the in-memory mirror too, so a stale Pinia projection can't
    // survive the localStorage wipe.
    clearMemoryCache()

    return results
  }

  /**
   * Invalidate related caches across multiple services for unlocked data elements
   *
   * If the backend provides `affectedCacheNamespaces` (via Unlock::ElementRegistry),
   * only those specific namespaces are cleared. Otherwise, falls back to the
   * coarse core-namespace wipe (`invalidateCoreDataCaches`). This is safe because:
   * - Unlocks are infrequent, user-initiated actions
   * - Cache clearing is cheap (localStorage.removeItem calls)
   * - Caches repopulate on next access via TTL-based fetching
   *
   * @param {Array} unlockedElements - Array of unlocked data element keys
   * @param {Object} options - Optional configuration
   * @param {Array} options.affectedCacheNamespaces - Backend-provided namespace strings to clear
   * @returns {Object} Summary of invalidation results
   */
  const invalidateRelatedCaches = (unlockedElements, options = {}) => {
    if (!Array.isArray(unlockedElements) || unlockedElements.length === 0) {
      return { invalidated: 0, services: [], elements: [] }
    }

    const backendNamespaces = options.affectedCacheNamespaces

    // No targeted namespaces from the backend → coarse-clear the whole core
    // surface through the shared routine (same path as auth transition + the
    // share-grant re-band bridge).
    if (!Array.isArray(backendNamespaces) || backendNamespaces.length === 0) {
      console.log(`[LocalStorageCache] No backend namespaces provided, clearing all core namespaces for ${unlockedElements.length} unlocked elements`)
      return { ...invalidateCoreDataCaches(), elements: unlockedElements }
    }

    // Backend provided targeted namespaces — clear exactly those.
    const startTime = performance.now()
    const results = { invalidated: 0, services: [], elements: unlockedElements, errors: [] }

    try {
      console.log(`[LocalStorageCache] Using backend-provided namespaces for ${unlockedElements.length} unlocked elements:`, backendNamespaces)

      for (const ns of backendNamespaces) {
        try {
          const cleared = clearCacheByNamespace(ns, '*')
          if (cleared > 0) {
            results.invalidated += cleared
            results.services.push({
              namespace: ns,
              cleared: cleared
            })
          }
        } catch (error) {
          console.error(`[LocalStorageCache] Failed to clear namespace '${ns}':`, error)
          results.errors.push({ namespace: ns, error: error.message })
        }
      }

      // Always clear in-memory cache after unlock to prevent stale Pinia state
      clearMemoryCache()

      const duration = performance.now() - startTime
      console.log(`[LocalStorageCache] Cache invalidation completed in ${duration.toFixed(2)}ms: ${results.invalidated} entries cleared across ${results.services.length} namespaces`)

      return results

    } catch (error) {
      console.error('[LocalStorageCache] Cross-service cache invalidation failed:', error)
      results.errors.push({
        service: 'cache_coordinator',
        error: error.message
      })
      return results
    }
  }
  
  // ================================
  // STORE INTERFACE
  // ================================
  
  return {
    // Configuration constants
    LOCAL_STORAGE_SIZE_LIMIT,
    LOCAL_STORAGE_WRITE_BUDGET,
    CACHE_BASE_PREFIX,
    CACHE_NAMESPACES,
    CACHE_NAMESPACE_SCHEMA_VERSIONS,
    CACHE_TTL_MS,

    // Core state
    available,
    enablePersistence,
    storageMetrics,
    recentAccessCache,  // NEW: Expose in-memory cache
    activeRequests,     // NEW: Expose active requests map
    
    // Tracking buffer state
    trackingBuffer,
    bufferedUntil,
    
    // Computed properties
    cacheHitRate,
    storageUtilization,
    
    // Tracking buffer actions
    generateTrackingBufferKey,
    isElementInBuffer,
    addElementsToTrackingBuffer,
    filterUnbufferedElements,
    clearTrackingBuffer,
    
    // In-memory LRU cache actions
    getMemoryCachedEntity,
    setMemoryCachedEntity,
    clearMemoryCachedEntity,
    getMemoryCachedKey,
    setMemoryCachedKey,
    clearMemoryCachedKey,
    clearMemoryCache,
    
    // Request deduplication actions
    getActiveRequest,
    setActiveRequest,
    generateRequestKey,
    deduplicatedRequest,
    
    // Core localStorage actions  
    generateCacheKey,
    storeData,
    fetchData,
    
    // Cross-service cache invalidation methods
    invalidateRelatedCaches,
    invalidateCoreDataCaches,
    clearCacheByNamespace,
    clearCacheWhere,

    // localStorage eviction and cleanup
    getAllCacheEntries,
    evictCacheEntries,
    cleanupExpiredEntries,
    getStorageStats,

    clearCache: (namespace, keyComponents = null) => {
      // Simple clearCache implementation for backwards compatibility
      if (keyComponents) {
        const cacheKey = generateCacheKey(namespace, keyComponents)
        try {
          localStorage.removeItem(cacheKey)
          return 1
        } catch (error) {
          console.error(`[LocalStorageStore] Failed to clear cache:`, error)
          return 0
        }
      }
      return 0
    },
    
    // Cache access tracking actions
    buildElementCombinationsForCache,
    trackCacheAccessDeferred,
    trackCacheAccessImmediate,
    trackCacheAccess,
    
    // Utility actions
    initialize
  }
})